import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, LucideIcon } from "lucide-react";
import VideoDemo from "./VideoDemo";
import { useAuthForm } from "@/contexts/AuthFormProvider";

interface Feature {
  step: string;
  title: string;
  subtitle: string;
  videoSrc?: string;
  color: string;
  description: string;
  highlights: string[];
  icon: LucideIcon;
}

interface FeatureSectionProps {
  feature: Feature;
  index: number;
  isActive: boolean;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
  feature,
  index,
  isActive
}) => {
  const { showRegisterForm } = useAuthForm();

  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const textY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <div
      ref={sectionRef}
      className="flex items-center justify-center relative py-12 md:py-16 overflow-hidden"
    >
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, -50]) }}
      >
        <motion.div 
          className="absolute inset-0 opacity-10"
          style={{
            background: `radial-gradient(ellipse at center, ${feature.color}, transparent 70%)`,
            scale: useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.2, 0.8])
          }}
        />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
        
        <motion.div 
          className={`space-y-8 ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}
          style={{ y: textY }}
        >
          
          <motion.div 
            className="flex items-center space-x-6"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0 }}
            viewport={{ once: false, margin: "0%" }}
          >
            <motion.div 
              className="text-8xl font-black"
              style={{ 
                color: feature.color,
                fontFamily: 'GilroyMedium',
                opacity: isActive ? 0.3 : 0.1,
                transition: 'opacity 0.5s ease-out'
              }}
            >
              {feature.step}
            </motion.div>
            <motion.div 
              className="flex-1 h-px bg-gradient-to-r from-gray-600 to-transparent"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: false, margin: "0%" }}
            />
          </motion.div>

          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            viewport={{ once: false, margin: "0%" }}
          >
            <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight">
              {feature.title}
            </h2>
            <h3 className="text-xl text-gray-400 font-medium">
              {feature.subtitle}
            </h3>
          </motion.div>

          <motion.p 
            className="text-lg text-gray-300 leading-relaxed max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            viewport={{ once: false, margin: "0%" }}
          >
            {feature.description}
          </motion.p>

          <motion.div 
            className="space-y-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            viewport={{ once: false, margin: "0%" }}
          >
            {feature.highlights.map((highlight: string, i: number) => (
              <motion.div 
                key={i} 
                className="flex items-center space-x-3"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.05 + i * 0.03 }}
                viewport={{ once: false, margin: "0%" }}
              >
                <motion.div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: feature.color }}
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.2, delay: 0.08 + i * 0.03 }}
                  viewport={{ once: false, margin: "0%" }}
                />
                <span className="text-gray-300">{highlight}</span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="pt-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            viewport={{ once: false, margin: "0%" }}
          >
            <motion.button
              onClick={() => {
                window.lintrk('track', { conversion_id: 23344122 });
                showRegisterForm();
              }}
              className="px-8 py-4 rounded-xl font-bold text-white shadow-lg transition-all duration-300"
              style={{
                background: `linear-gradient(135deg, ${feature.color}, ${feature.color}CC)`,
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden'
              }}
              whileHover={{
                scale: 1.05,
                y: -2,
                boxShadow: `0 10px 30px ${feature.color}40`
              }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center space-x-2">
                <span>Experience This Feature</span>
                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.div>
              </div>
            </motion.button>
          </motion.div>
        </motion.div>

        <div className={`${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
          <VideoDemo
            title={feature.title}
            videoSrc={feature.videoSrc}
          />
        </div>
      </div>
    </div>
  );
};

export default FeatureSection;